import React from 'react'
import {
  type CrossClientQueryResultAnswer,
  type CrossClientQueryLogicalQuestionDecomposition,
  CrossClientQueryResultMatches,
} from '../../../../models/CrossClient'
import { type User } from '../../../../models/User'
import { useAppSelector } from '../../../../store/hooks'
import { selectCrossClientResultsExecution, selectCrossClientSelectedTab, selectCrossClientTeammates } from '../../../../store/crossClient/crossClientResults'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { CrossClientResultSource } from '../../Results/Sources/CrossClientResultSource'
import { CrossClientResultMatch } from './CrossClientResultMatch'
import { getUserDisplayName } from '../../../../apps/professional-app/fa/common/aifeatures/utils'
import { getOwnerLabel } from '../../utils'
import { CrossClientResultButtons } from './CrossClientResultButtons'
import { CrossClientViewProfileButton } from './CrossClientViewProfileButton'
import { CrossClientChangeMatchButton } from './CrossClientChangeMatchButton'
import { CrossClientContinueButton } from '../CrossClientContinueButton'
import { crossClientNextStepLeadCopy, getCrossClientNextStep } from '../crossClientNextStep'
import { CrossClientQueryResultDetails } from './hooks'
import { CrossClientResultNavButtons } from './CrossClientResultNavButtons'
import { CrossClientResultAnswerExplanation } from './CrossClientResultAnswerExplanation'

const hasDefinitiveAnswer = (answers: CrossClientQueryResultAnswer[] | null | undefined): boolean => (answers ?? []).some((answer) => answer.decision !== null)

export interface CrossClientResultDetailsProps {
  client: User
  decomposition: CrossClientQueryLogicalQuestionDecomposition
  resultDetails: CrossClientQueryResultDetails
}

export const CrossClientResultDetails: React.FC<CrossClientResultDetailsProps> = ({ client, decomposition, resultDetails }) => {
  const { selectedResult, hasNextResult, hasPreviousResult, handleNextResult, handlePreviousResult, handleSelectedResultUpdated } = resultDetails
  const execution = useAppSelector(selectCrossClientResultsExecution)
  const selectedTab = useAppSelector(selectCrossClientSelectedTab)
  const teammates = useAppSelector(selectCrossClientTeammates)
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)

  if (!selectedResult) {
    return null
  }

  const { answers, bulkResolverAnswers, localResolverAnswers, match, evaluationType } = selectedResult
  const isPotential = match === CrossClientQueryResultMatches.POTENTIAL
  const potentialAnswers =
    isPotential && localResolverAnswers != null && !hasDefinitiveAnswer(localResolverAnswers) ? bulkResolverAnswers : localResolverAnswers
  const displayAnswers = answers ?? potentialAnswers ?? bulkResolverAnswers ?? null
  const name = getUserDisplayName(client)
  const firstName = client.firstName || name
  const isNoMatch = match === CrossClientQueryResultMatches.NO_MATCH
  const nextStep = getCrossClientNextStep(selectedTab, selectedResult, execution)
  const ownerLabel = getOwnerLabel(currentUser, teammates, client.owner?.ownerId ?? null)

  return (
    <div className='d-flex flex-column gap-4 py-2 zocks-mb-2'>
      <div className='d-flex flex-column gap-3 p-3 zocks-bg-color-gray-100 border border-1 zocks-border-color-gray-300 zocks-border-1.0'>
        <div className='d-flex gap-3 align-items-start justify-content-between'>
          <div className='d-flex flex-column gap-1'>
            <div className='d-flex gap-1 align-items-center'>
              <div className='paragraph-medium'>{name}</div>
              <CrossClientResultMatch className='px-2 py-1 gap-1 cross-client-details-match' match={match} iconWidth={12} iconHeight={12} />
              {selectedResult.matchSetManuallyAt ? <span className='paragraph-small zocks-text-gray-600'>(set manually)</span> : null}
            </div>
            <div className='d-flex flex-wrap align-items-center gap-2 paragraph-small zocks-text-gray-600'>
              <span>{client?.email ?? ''}</span>
              {ownerLabel !== '-' ? (
                <>
                  <span aria-hidden='true'>•</span>
                  <span>Owner: {ownerLabel}</span>
                </>
              ) : null}
            </div>
          </div>
          <CrossClientResultNavButtons
            hasPreviousResult={hasPreviousResult}
            hasNextResult={hasNextResult}
            handlePreviousResult={handlePreviousResult}
            handleNextResult={handleNextResult}
          />
        </div>
        <hr className='my-0 zocks-border-color-gray-500' />
        {isPotential ? (
          <div className='d-flex flex-column gap-2'>
            <div className='paragraph-medium zocks-text-gray-900'>{crossClientNextStepLeadCopy(nextStep, firstName)}</div>
            <div className='d-flex'>
              <CrossClientContinueButton result={selectedResult} withTooltip={false} className='px-3' />
            </div>
            <div className='paragraph-medium zocks-text-gray-900'>Or you can review {firstName} manually and change the match if needed.</div>
            <div className='d-flex gap-2 align-items-center'>
              <CrossClientViewProfileButton client={client} />
              <CrossClientChangeMatchButton result={selectedResult} onChanged={handleSelectedResultUpdated} />
            </div>
          </div>
        ) : (
          <div className='d-flex gap-2 align-items-center'>
            {isNoMatch ? null : (
              <CrossClientResultButtons result={selectedResult} className='bg-white border border-1 zocks-border-color-gray-300 zocks-border-0.5' withText />
            )}
            <CrossClientViewProfileButton client={client} />
            <CrossClientChangeMatchButton result={selectedResult} onChanged={handleSelectedResultUpdated} />
          </div>
        )}
      </div>
      {!displayAnswers ? (
        <div>No data available for this client</div>
      ) : (
        decomposition.atomicClauses
          .map((clause, index) => ({ clause, answer: displayAnswers[index] ?? { decision: null, explanation: '' }, index }))
          .filter(({ answer }) => match !== CrossClientQueryResultMatches.POTENTIAL || !!answer?.source)
          .map(({ clause, answer, index }) => (
            <div key={index} className='d-flex flex-column gap-2'>
              <div className='d-flex flex-column gap-1'>
                <div className='d-flex gap-2'>
                  <i className='bi bi-x-diamond-fill zocks-text-info' />
                  <div className='paragraph-medium medium'>{clause.questionForm}</div>
                </div>
                <div className='paragraph-medium ms-1 ps-3'>
                  <CrossClientResultAnswerExplanation answer={answer} match={match} evaluationType={evaluationType} />
                </div>
              </div>
              {answer.source ? <CrossClientResultSource resultSource={answer.source} /> : null}
            </div>
          ))
      )}
    </div>
  )
}
